import { useTranslation } from 'react-i18next';

import { APIError } from '@/lib/api';

/**
 * Translate an error thrown by the API client into a user-facing string.
 *
 * The backend returns short code-style detail strings (`insufficient_role`,
 * `fuseki_unavailable`, …) which are mapped to localised messages in the
 * `errors` namespace. Unknown codes fall through to the generic message so
 * we never surface a raw backend string to the user.
 *
 * Usage:
 *   const apiError = useApiError();
 *   const message = apiError(mutation.error);
 */
export function useApiError(): (error: unknown) => string | null {
  const { t } = useTranslation('errors');

  return (error: unknown): string | null => {
    if (!error) return null;
    if (error instanceof APIError) {
      const detail = error.detail;
      if (typeof detail === 'string') {
        // i18next returns the key itself when missing — fall back to `generic`.
        const translated = t(detail, { defaultValue: '' });
        return translated || t('generic');
      }
      if (detail && typeof detail === 'object') {
        return JSON.stringify(detail);
      }
      return t('generic');
    }
    if (error instanceof Error) {
      return error.message || t('generic');
    }
    return t('generic');
  };
}
